import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';

export default function Cattle() {
  const { isOwner } = useOutletContext();
  const [list, setList] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/api/cattle${showAll ? '?all=1' : ''}`)
      .then(({ cattle }) => setList(cattle))
      .catch(e => setErr(e.message));
  }, [showAll]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!list) return <div className="text-ink-700/60 italic">Loading…</div>;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Cattle</h1>
        <div className="flex items-center gap-3">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Include sold/deceased/culled
          </label>
          {isOwner && <Link to="/cattle/import" className="btn-ghost">Import ADCA</Link>}
          {isOwner && <Link to="/cattle/new" className="btn-primary">+ Add</Link>}
        </div>
      </header>

      {list.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-ink-700/70">No cattle on file.</p>
          {isOwner && <Link to="/cattle/import" className="btn-primary mt-4">Import from ADCA CSV</Link>}
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map(c => (
            <li key={c.id} className="card">
              <Link to={`/cattle/${c.id}`} className="block">
                <div className="flex items-start gap-3">
                  <SexBadge sex={c.sex} />
                  <div className="flex-1">
                    <div className="font-medium text-ink-900">
                      {c.name || c.ear_tag || `#${c.id}`}
                      {c.adca_reg_no && <span className="text-xs text-ink-700/50 ml-2">ADCA {c.adca_reg_no}</span>}
                      {c.disposition !== 'active' && (
                        <span className="ml-2 pill bg-ink-700/10 text-ink-700">{c.disposition}</span>
                      )}
                    </div>
                    <div className="text-sm text-ink-700/70 mt-0.5">
                      {c.dob && <span>DOB {fmtDate(c.dob)}</span>}
                      {c.color && <span> · {c.color}</span>}
                      {c.ear_tag && c.name && <span> · tag {c.ear_tag}</span>}
                    </div>
                    <div className="text-xs text-ink-700/60 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {c.polled_status && <span>Polled: <strong>{c.polled_status}</strong></span>}
                      {c.a2_status && <span>A2: <strong>{c.a2_status}</strong></span>}
                      {c.pha_status && <span>PHA: {c.pha_status}</span>}
                      {c.sire_name && <span>Sire: {c.sire_name}</span>}
                      {c.dam_name && <span>Dam: {c.dam_name}</span>}
                      {c.event_count > 0 && <span>{c.event_count} event{c.event_count === 1 ? '' : 's'}</span>}
                    </div>
                  </div>
                  <div className="text-ink-700/40">›</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SexBadge({ sex }) {
  if (!sex) return <span className="pill bg-ink-700/10 text-ink-700">?</span>;
  const map = {
    bull:   { label: 'Bull',   cls: 'bg-rust-500/15 text-rust-700' },
    cow:    { label: 'Cow',    cls: 'bg-olive-100 text-olive-900' },
    steer:  { label: 'Steer',  cls: 'bg-gold-500/15 text-gold-600' },
    heifer: { label: 'Heifer', cls: 'bg-olive-100 text-olive-900' },
    calf:   { label: 'Calf',   cls: 'bg-cream-100 text-ink-700' },
  };
  const m = map[sex] || { label: sex, cls: 'bg-ink-700/10 text-ink-700' };
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}
